import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Ticket } from 'lucide-react';
interface Flyer {
  id: string;
  title: string;
  schedule: string;
  image: string;
  href: string;
}
const flyers: Flyer[] = [{
  id: 'theatre-fridays',
  title: 'Theatre Fridays',
  schedule: 'Fridays • The Weekend Starts Here',
  image: 'https://cdn.magicpatterns.com/uploads/vFoLKQt7tmQNeW42491i3H/Screenshot_2026-06-03_121032.png',
  href: '/events'
}, {
  id: 'sunday-brunch',
  title: 'Sunday Brunch',
  schedule: 'Sundays • All You Can Eat',
  image: 'https://cdn.magicpatterns.com/uploads/1UnSRj778xTSEKhZj3RWW2/Brunch_is_cool.But_brunch_with_music,_energy,_cocktails,_and_your_peopleThat’s_a_lifestyle.All_Y.jpg',
  href: '/brunch'
}, {
  id: 'melodies-mimosas-fats',
  title: 'Melodies & Mimosas',
  schedule: 'Sundays • Hosted by FatsDaBarber',
  image: 'https://cdn.magicpatterns.com/uploads/egvtu2RZh7jyk4VrcNfkZs/Screenshot_2026-06-03_121131.png',
  href: '/brunch'
}, {
  id: 'melodies-mimosas-jay',
  title: 'Melodies & Mimosas',
  schedule: 'Sundays • Featuring Jay Cole',
  image: 'https://cdn.magicpatterns.com/uploads/5S6yLELAjwmUT7HgMM4TwU/Screenshot_2026-06-03_121150.png',
  href: '/brunch'
}, {
  id: 'rum-punch-suya',
  title: 'Rum Punch & Suya',
  schedule: 'Sundays • Theatre Sundays',
  image: 'https://cdn.magicpatterns.com/uploads/fDpzvrHSUUb7wem2YitCTV/Stay_tuned_after_Sunday’s_All_You_Can_Eat_Brunch_for_a_special_Mother’s_Day_Edition_of_Rum_Punch.jpg',
  href: '/events'
}];
export function FeaturedFlyers() {
  return <section className="py-16 md:py-20 bg-[#0B1014] relative overflow-hidden" data-id="element-380">
      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-381">
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#DDA247] rounded-full blur-3xl" data-id="element-382" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#DDA247] rounded-full blur-3xl" data-id="element-383" />
      </div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-384">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10 md:mb-14" data-id="element-385">
          <p className="text-[#DDA247] text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-3" data-id="element-386">
            On The Marquee
          </p>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight uppercase mb-4" data-id="element-387">
            Featured Events
          </h2>
          <div className="w-16 md:w-24 h-1 bg-[#DDA247] mx-auto mb-5" data-id="element-388" />
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-389">
            Catch the latest from the Theatre of Dreams. Tap a flyer to see
            what's coming up and lock in your spot.
          </p>
        </motion.div>

        {/* Uniform Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-390">
          {flyers.map((flyer, index) => <motion.div key={flyer.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.6
        }} data-id="element-391">
              <FlyerCard flyer={flyer} data-id="element-392" />
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center mt-10 md:mt-14" data-id="element-393">
          <Link to="/events" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#DDA247] text-[#0B1014] font-bold text-sm uppercase tracking-widest rounded-full hover:bg-white transition-colors duration-300" data-id="element-394">
            <Ticket className="w-4 h-4" data-id="element-395" />
            See All Events
          </Link>
        </motion.div>
      </div>
    </section>;
}
function FlyerCard({
  flyer
}: {
  flyer: Flyer;
}) {
  return <Link to={flyer.href} className="group relative block w-full h-full overflow-hidden rounded-2xl border border-white/10 bg-[#11161A] shadow-xl hover:border-[#DDA247]/50 transition-all duration-300" aria-label={`${flyer.title} — ${flyer.schedule}`} data-id="element-396">
      <div className="relative w-full aspect-[3/4] overflow-hidden" data-id="element-397">
        <img src={flyer.image} alt={`${flyer.title} event flyer`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-398" />
        {/* Gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014] via-[#0B1014]/10 to-transparent" data-id="element-399" />

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 p-4 md:p-5" data-id="element-400">
          <h3 className="text-white font-black uppercase tracking-tight leading-tight text-lg md:text-xl" data-id="element-401">
            {flyer.title}
          </h3>
          <p className="text-[#DDA247] text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1" data-id="element-402">
            {flyer.schedule}
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-white text-xs font-bold uppercase tracking-wider opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" data-id="element-403">
            View Event <ArrowRight className="w-3.5 h-3.5" data-id="element-404" />
          </span>
        </div>

        {/* Hover glow ring */}
        <div className="absolute inset-0 rounded-2xl ring-0 group-hover:ring-2 ring-[#DDA247]/40 transition-all duration-300 pointer-events-none" data-id="element-405" />
      </div>
    </Link>;
}